// @ts-nocheck
"use client";
import { useState, useEffect, useRef } from "react";
import { FlowCanvas } from "./FlowCanvas";

/* ───────────────────────── Reveal helper ───────────────────────── */
function Reveal({ children, delay = 0, y = 24, as: As = "div", className, style }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((es) => {
      es.forEach(e => { if (e.isIntersecting) { setShown(true); io.disconnect(); } });
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <As
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity 900ms cubic-bezier(.2,.7,.2,1) ${delay}ms, transform 900ms cubic-bezier(.2,.7,.2,1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </As>
  );
}

/* ───────────────────────── Theme switch ───────────────────────── */
function ThemeSwitch({ theme, onChange }) {
  const opts = [
    { v: "blueDark", label: "Blue", icon: "●", color: "#2563eb" },
    { v: "night",    label: "Night", icon: "●", color: "#0E0E0E" },
    { v: "light",    label: "Light", icon: "●", color: "#f2f4fb" },
  ];
  return (
    <div className="ork-theme-switch" role="radiogroup" aria-label="Theme">
      {opts.map(o => (
        <button
          key={o.v}
          role="radio"
          aria-checked={theme === o.v}
          data-active={theme === o.v || undefined}
          onClick={() => onChange(o.v)}
          title={o.label}
        >
          <span className="ork-theme-dot" style={{ background: o.color }} />
          <span className="ork-theme-lbl">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/* Mapeo theme landing → variante del mark */
const LANDING_MARK: Record<string, string> = {
  blueDark: "/landing/orkestria-mark-light.png",
  dark:     "/landing/orkestria-mark-light.png",
  night:    "/landing/orkestria-mark-cream.png",
  light:    "/landing/orkestria-mark-dark.png",
};

/* ───────────────────────── Nav ───────────────────────── */
function Nav({ theme, onTheme, onContact }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav className="ork-nav" data-scrolled={scrolled || undefined}>
      <div className="ork-nav-inner">
        <a className="ork-nav-brand" href="#top">
          <img
            className="ork-nav-mark"
            src={LANDING_MARK[theme] ?? LANDING_MARK.blueDark}
            alt="Orkestria"
          />
          <span className="ork-nav-brand-name">Orkestria</span>
        </a>
        <div className="ork-nav-links">
          <a href="#problema">Problema</a>
          <a href="#sistema">Sistema</a>
          <a href="#equipo">Equipo</a>
          <a href="#faki">Faki</a>
          <a href="#planes">Planes</a>
          <a href="/docs">Docs</a>
          <a href="#cta">Demo</a>
        </div>
        <div className="ork-nav-cta">
          <ThemeSwitch theme={theme} onChange={onTheme} />
          <a className="ork-btn-ghost" href="/auth/login">Iniciar sesión</a>
          <button
            type="button"
            className="ork-btn-primary"
            onClick={() => onContact && onContact("access")}
          >
            Solicitar acceso<span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </nav>
  );
}

/* ───────────────────────── Hero ───────────────────────── */
// Mapeo theme → archivo de video (renderizado por Remotion en orkestria-video)
const VIDEO_BY_THEME = {
  blueDark: "/landing/hero-video/hero-dark.mp4",
  night:    "/landing/hero-video/hero-night.mp4",
  light:    "/landing/hero-video/hero-light.mp4",
};

function Hero({ glow, density, autoOrbit, palette, style, theme, onContact }) {
  // phase 0: FlowCanvas anim — phase 1: video producto — phase 2: FlowCanvas anim en loop infinito
  const [phase, setPhase] = useState(0);
  const videoRef = useRef(null);

  // Tras ~14s (una vuelta de la animación) → arranca el video producto
  useEffect(() => {
    if (phase !== 0) return;
    const t = setTimeout(() => setPhase(1), 14000);
    return () => clearTimeout(t);
  }, [phase]);

  // Cuando cambiamos a phase 1, intentamos reproducir el video desde el principio
  useEffect(() => {
    if (phase !== 1) return;
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {}
  }, [phase]);

  // Si cambia el theme mientras el video está visible, recargar el src y reproducir
  useEffect(() => {
    if (phase !== 1) return;
    const v = videoRef.current;
    if (!v) return;
    try {
      v.load();
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {}
  }, [theme, phase]);

  // Re-orquestar: toggle inmediato entre anim y video.
  //   · Si anim está corriendo (phase 0 o 2) → salta directo al video.
  //   · Si el video está corriendo (phase 1) → reinicia la anim (que tras
  //     14s volverá a llamar al video naturalmente).
  const replay = () => {
    setPhase((p) => (p === 1 ? 0 : 1));
  };

  const videoSrc = VIDEO_BY_THEME[theme] || VIDEO_BY_THEME.blueDark;

  return (
    <section className="ork-hero" id="top">
      {/* Background layers */}
      <div className="ork-hero-bg">
        {/* Video del producto — phase 1 */}
        <div className="ork-hero-video" data-on={phase === 1 || undefined}>
          <video
            ref={videoRef}
            key={theme /* recarga cuando cambia paleta */}
            src={videoSrc}
            muted
            playsInline
            preload="auto"
            onEnded={() => setPhase(2)}
            aria-hidden
          />
        </div>
        {/* Flow canvas — phase 0 (primera vuelta) y phase 2 (loop infinito tras el video) */}
        <div className="ork-hero-flow" data-on={phase !== 1 || undefined}>
          <FlowCanvas glow={glow} density={density} autoOrbit={autoOrbit} palette={palette} style={style} theme={theme} />
        </div>
        {/* Orkestria logo mark — replaces the cyan orb. Sits in front of flow, behind title. */}
        <div className="ork-hero-mark" data-on={phase !== 1 || undefined} aria-hidden>
          <div className="ork-hero-mark-img" />
        </div>
        <div className="ork-hero-vignette" />
      </div>

      {/* Replay control (subtle) */}
      <button className="ork-replay" onClick={replay} aria-label="Volver a orquestar">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" />
        </svg>
        <span>Volver a orquestar</span>
      </button>

      {/* Bottom-anchored content */}
      <div className="ork-hero-content">
        <Reveal delay={1700} y={60} as="h1" className="ork-hero-title">
          <span className="ork-hero-titleline">
            <img className="ork-hero-O" src="/landing/orkestria-O-light.png" alt="" aria-hidden />
            <span className="ork-hero-titleline-rest">rkestria</span>
          </span>
        </Reveal>

        <div className="ork-hero-meta">
          <Reveal delay={2100} y={20}>
            <p className="ork-hero-sub">
              Orquesta la operación de tu restaurante.
            </p>
            <p className="ork-hero-supp">
              Pedidos, cocina, caja, facturación, carta digital y analítica &mdash; en una sola plataforma sincronizada en tiempo real.
            </p>
          </Reveal>

          <Reveal delay={2400} y={20} className="ork-hero-actions">
            <button
              type="button"
              className="ork-btn-primary lg"
              onClick={() => onContact && onContact("access")}
            >
              Solicitar acceso <span aria-hidden>→</span>
            </button>
            <button
              type="button"
              className="ork-btn-ghost lg"
              onClick={() => onContact && onContact("demo")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="m6 4 14 8-14 8z" fill="currentColor" />
              </svg>
              Solicitar demo
            </button>
          </Reveal>
        </div>
      </div>

    </section>
  );
}

/* ───── Demo placeholder — striped wireframe of dashboard ─────
   Now scroll-driven: as user scrolls past the hero, the dashboard slides
   downward and fades behind the ORKESTRIA letters. Reverses on scroll up. */
function DemoPlaceholder() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const sectionEl = el.closest(".ork-hero");
      if (!sectionEl) return;
      const rect = sectionEl.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // progress: 0 when hero fully in view, 1 when scrolled fully past
      // we measure how much of the hero has scrolled past the top of viewport
      const scrolled = Math.max(0, -rect.top);
      const total = Math.max(1, rect.height * 0.7);
      const p = Math.min(1, scrolled / total);
      // Easing
      const eased = p * p * (3 - 2 * p); // smoothstep
      el.style.setProperty("--demo-p", eased.toFixed(3));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div className="ork-demo-shot" ref={ref} aria-label="Captura del producto · placeholder">
      <div className="ork-demo-window">
        <div className="ork-demo-chrome">
          <span /><span /><span />
          <em>orkestria.app · dashboard</em>
        </div>

        {/* App header — refleja el DashboardNavbar real */}
        <div className="ork-demo-appbar">
          <div className="ork-demo-appbar-brand">
            <div className="ork-demo-appbar-logo" />
            <div className="ork-demo-appbar-title">
              <b>Orkestria</b>
              <span>Panel administración</span>
            </div>
          </div>
          <div className="ork-demo-appbar-right">
            <span className="ork-demo-appbar-bell" />
            <div className="ork-demo-appbar-user">
              <span>Carlos</span>
              <i>C</i>
            </div>
          </div>
        </div>

        {/* Nav pills — perspectiva de usuario (dueño de restaurante) */}
        <div className="ork-demo-nav">
          {["Resumen","Pedidos","Cocina","Caja","Carta","Reservas","Productos","Analítica"].map((l, i) => (
            <span key={l} data-active={i === 0 || undefined}>{l}</span>
          ))}
        </div>

        <div className="ork-demo-body">
          <div className="ork-demo-main">
            {/* Header con info restaurante + selector de rango */}
            <div className="ork-demo-resthead">
              <div className="ork-demo-resthead-info">
                <div className="ork-demo-resthead-logo" />
                <div>
                  <b>Restaurante Lúmina</b>
                  <span>Cra. 11 #82-15 · COP</span>
                </div>
              </div>
              <div className="ork-demo-range">
                <span data-active>30 días</span>
                <span>90 días</span>
                <span>1 año</span>
              </div>
            </div>

            {/* KPIs */}
            <div className="ork-demo-kpis">
              {[
                ["Ingresos","$74.250.000","+18,2%", true],
                ["Órdenes","1.482","+22%", true],
                ["Ticket prom.","$58.300","+4,1%", true],
                ["Items vendidos","3.184","+11%", true],
              ].map(([label, val, delta, up]) => (
                <div key={label} className="ork-demo-kpi">
                  <span>{label}</span>
                  <b>{val}</b>
                  <em className={up ? "up" : "down"}>{delta}</em>
                </div>
              ))}
            </div>

            {/* Chart principal */}
            <div className="ork-demo-card-wrap">
              <div className="ork-demo-card-h">
                <b>Ingresos por día</b>
                <span>Últimos 30 días</span>
              </div>
              <div className="ork-demo-chart">
                <svg viewBox="0 0 600 180" preserveAspectRatio="none" width="100%" height="100%">
                  <defs>
                    <linearGradient id="ag" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor="#2563eb" stopOpacity=".45" />
                      <stop offset="1" stopColor="#2563eb" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[0,1,2,3].map(i => <line key={i} x1="0" x2="600" y1={36 + i*36} y2={36 + i*36} stroke="currentColor" strokeOpacity=".06" />)}
                  <path d="M0,140 C60,115 100,128 160,98 C220,68 260,118 320,78 C380,40 440,72 500,52 C540,40 580,62 600,48 L600,180 L0,180 Z" fill="url(#ag)" />
                  <path d="M0,140 C60,115 100,128 160,98 C220,68 260,118 320,78 C380,40 440,72 500,52 C540,40 580,62 600,48" fill="none" stroke="#2563eb" strokeWidth="1.8" />
                </svg>
              </div>
            </div>

            {/* Fila inferior: 2 cards */}
            <div className="ork-demo-bottom">
              <div className="ork-demo-card-wrap">
                <div className="ork-demo-card-h">
                  <b>Métodos de pago</b>
                  <span>Distribución</span>
                </div>
                <div className="ork-demo-donut">
                  <svg viewBox="0 0 100 100" width="80" height="80">
                    <circle cx="50" cy="50" r="36" fill="none" stroke="var(--ork-line-strong)" strokeWidth="14"/>
                    <circle cx="50" cy="50" r="36" fill="none" stroke="#2563eb" strokeWidth="14"
                      strokeDasharray="120 226" strokeDashoffset="0" transform="rotate(-90 50 50)"/>
                    <circle cx="50" cy="50" r="36" fill="none" stroke="#10b981" strokeWidth="14"
                      strokeDasharray="60 226" strokeDashoffset="-120" transform="rotate(-90 50 50)"/>
                    <circle cx="50" cy="50" r="36" fill="none" stroke="#f59e0b" strokeWidth="14"
                      strokeDasharray="46 226" strokeDashoffset="-180" transform="rotate(-90 50 50)"/>
                  </svg>
                  <div className="ork-demo-donut-legend">
                    <div><i style={{ background: "#2563eb" }} />Tarjeta<em>53%</em></div>
                    <div><i style={{ background: "#10b981" }} />Efectivo<em>27%</em></div>
                    <div><i style={{ background: "#f59e0b" }} />Transfer<em>20%</em></div>
                  </div>
                </div>
              </div>

              <div className="ork-demo-card-wrap">
                <div className="ork-demo-card-h">
                  <b>Pedidos recientes</b>
                  <span>Tiempo real</span>
                </div>
                <div className="ork-demo-cards">
                  {[
                    { id: "#048", l: "Mesa 12 · En cocina", st: 0, t: "3m" },
                    { id: "#049", l: "Domicilio · Listo", st: 1, t: "6m" },
                    { id: "#050", l: "Mesa 4 · Entregado", st: 2, t: "11m" },
                  ].map((c) => (
                    <div key={c.id} className="ork-demo-card">
                      <span data-st={c.st} />
                      <b>{c.id} · {c.l}</b>
                      <em>{c.t}</em>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ork-demo-tag"><span>·</span> placeholder · próximamente video real</div>
    </div>
  );
}

export { Nav, Hero, Reveal };
